import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { generateClient } from '@aws-amplify/api';
import { getRecruitmentSources as _getRecruitmentSources, getQueueTasks as _getQueueTasks } from '@/graphql/queries';
import FilterContext from '@/FilterContext';
import useFilter from '@/hooks/useFilter';
import useSubscription from '@/hooks/useSubscription';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faPhone,
  faSms,
  faSort
} from '@fortawesome/free-solid-svg-icons';
import { getDashboardStats } from '@/apis/mockDbCrud';
import { dash } from '@/utils';

const client = generateClient();

const getQueueTasks = ({ filters, limit, sorting } = {}) => {
  return client.graphql({ query: _getQueueTasks, variables: { filters, limit, sorting }, sorting: { field: 'estimated_first_attempt_at', order: 'asc' } })
    .then(response => {
      return response.data.getQueueTasks?.queue_task
    });
};

const NULL_RECRUITMENT_SOURCE = {
    id: null,
    source_name: 'No Recruitment Source'
};

const NULL_CAMPAIGN = {
    id: null,
    source_name: 'No Campaign'
};

const getRecruitmentSources = ({ parent_id } = {}) => {
    const filters = [
        ...(parent_id !== undefined ? [{ parent_id: { eq: parent_id } }] : [])
    ];
    return client.graphql({ query: _getRecruitmentSources, variables: { filters, sorting: { field: 'source_name', order: 'asc' } } })
        .then(response => {
            return [
                NULL_RECRUITMENT_SOURCE,
                ...response.data.getRecruitmentSources.recruitment_sources
            ];
        });
};

const ICON_BY_GROUP = {
  'Voice Inbound': faPhone,
  'Voice Outbound': faPhone,
  'Text Inbound': faSms,
  'Text Outbound': faSms,
  'Email Inbound': faEnvelope,
  'Email Outbound': faEnvelope
};

export default function AgentActionQueueExpandedView() {
  const isConstructed = useRef(false);
  const navigate = useNavigate();
  const filterContext = useFilter({});
  const [modal] = useState();
  const [hover, setHover] = useState(false);
  const [rows, setRows] = useState([]);
  const [recruitmentSources, setRecruitmentSources] = useState();
  const [range, setRange] = useState('1');
  const [recruitmentId, setRecruitmentId] = useState();

  const filters_ref = useRef();
  filters_ref.current = useMemo(() => {
    return {
      filters: [{
        estimated_first_attempt_at: { lte: moment().add(range, 'days').toDate() },
        ...(recruitmentId ? { recruitment_id: { eq: recruitmentId } } : undefined)
      }, {
        estimated_first_attempt_at: { eq: null },
        ...(recruitmentId ? { recruitment_id: { eq: recruitmentId } } : undefined)
      }],
      sorting: { field: 'estimated_first_attempt_at', order: 'asc' }
    };
  }, [range, recruitmentId, recruitmentSources]);
  useSubscription({ filters_ref, rankings_key: 'queue_task', setRows });

  const load = useCallback(() => {
    const { filters, sorting } = filters_ref.current;
    getQueueTasks({
      filters,
      sorting
    })
      .then(res => {
        setRows(res);
      });
  }, [filters_ref.current]);

  if (!isConstructed.current) {
    isConstructed.current = true;
    getRecruitmentSources()
      .then(setRecruitmentSources);
  }

  const handleRange = option => {
    setRange(option);
  };

  const handleRecruitmentId = option => {
    setRecruitmentId(option ? parseInt(option, 10) : null);
  };

  useEffect(() => {
    load();
  }, [range, recruitmentId]);

  /*
  const data = [
    { channel: "Voice Inbound", subject: "Anna Roberts", action: "Call Appointment", scheduled: "07/22/2025 at 10:00 AM", lastInteraction: "07/02/2025" },
    { channel: "Voice Outbound", subject: "James Wilson", action: "Follow-up Message", scheduled: "07/22/2025 at 10:30 AM", lastInteraction: "07/02/2025" },
    { channel: "Text Inbound", subject: "Laura Martinez", action: "Initial Outreach", scheduled: "07/22/2025 at 11:00 AM", lastInteraction: "07/02/2025" },
    { channel: "Text Outbound", subject: "Peter Evans", action: "Intake Interview", scheduled: "07/22/2025 at 12:00 PM", lastInteraction: "07/02/2025" },
    { channel: "Email Inbound", subject: "Sophia Carter", action: "Schedule Reminder", scheduled: "07/22/2025 at 12:15 PM", lastInteraction: "07/02/2025" },
    { channel: "Email Outbound", subject: "Daniel Hughes", action: "Confirm Appointment", scheduled: "07/22/2025 at 1:00 PM", lastInteraction: "07/02/2025" }
  ];
  */

  const stickyThStyle = {
    position: 'sticky',
    top: 0,
    backgroundColor: '#F4F4F4', 
    zIndex: 2,
    fontSize: '0.95rem',
    textAlign: 'left'
  };

  const HeadCell = ({ children, className = '' }) => (
    <th style={stickyThStyle} className={`py-2 text-start ${className}`} scope="col">
      <div className="d-inline-flex align-items-center justify-content-start gap-1">
        <span>{children}</span>
        <FontAwesomeIcon icon={faSort} style={{ color: '#2E2868', cursor: 'pointer', fontSize: '0.8em' }} />
      </div>
    </th>
  );

  return (
    <FilterContext.Provider value={filterContext}>
      {modal}
      <div className="container-fluid px-4 pt-5 pb-3 d-flex flex-column" style={{ minHeight: '100vh' }}>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          {/* Left: title */}
          <h5 className="fw-semibold mb-0 ps-2" style={{ color: '#2E2868', fontSize: '1.4375rem', textAlign: 'left' }}>
            Agent Action Queue
          </h5>

          {/* Right: filters + Close */}
          <div className="d-flex align-items-center gap-2">
            <select className="form-select form-select-sm w-auto" onClick={e => handleRange(e.target.value)}>
              <option value="1">Next 24 Hours</option>
              <option value="7">Next 7 Days</option>
              <option value="30">Next 30 Days</option>
            </select>

            <select className="form-select form-select-sm w-auto" onClick={e => handleRecruitmentId(e.target.value)}>
              {(recruitmentSources || []).map((e, i) => (<option key={i} value={e.id || ''}>{e.source_name}</option>))}
            </select>

            <button
              className="btn btn-sm"
              style={{
                backgroundColor: '#2E2868',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 12px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
              onClick={() => navigate('/')}
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="border rounded-3 overflow-auto" style={{ maxHeight: 'calc(100vh - 150px)' }}>
          <table
            className="table table-sm table-borderless align-middle mb-0"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.875rem'
            }}
          >
            <thead  style={{ height: '30px',  }}>
              <tr className="border-bottom">
                <HeadCell className="ps-3 text-start align-middle" style={{ borderRight: '1px solid #ccc' }}>Channel</HeadCell>
                <HeadCell  className="ps-3 text-start align-middle" style={{ borderRight: '1px solid #ccc' }}>Subject</HeadCell>
                <HeadCell  className="ps-3 text-start align-middle" style={{ borderRight: '1px solid #ccc' }}>Scheduled For</HeadCell>
                <HeadCell  className="ps-3 text-start align-middle" style={{ borderRight: '1px solid #ccc' }}>Status</HeadCell>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="border-bottom" style={{ height: '30px' }}>
                  <td className="text-start align-middle py-2 ps-3" style={{ borderRight: '1px solid #ccc' }}>
                    {row.channel}
                    {ICON_BY_GROUP[row.channel] &&
                      <FontAwesomeIcon
                        className="text-dark ms-2"
                        icon={ICON_BY_GROUP[row.channel]}
                        size="lg"
                        title={row.channel}
                      />
                    }
                  </td>
                  <td className="text-start align-middle py-2 ps-3" style={{ borderRight: '1px solid #ccc' }}>
                    <span className="fw-semibold text-muted">{row.full_name_first}</span>
                  </td>
                  <td className="text-start align-middle py-2 ps-3" style={{ borderRight: '1px solid #ccc' }}>{row.estimated_first_attempt_at ? moment(row.estimated_first_attempt_at).format('M/D/YY h:mm A') : dash}</td>
                  <td className="text-start align-middle py-2 ps-3" style={{ borderRight: '1px solid #ccc' }}>{row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>

        </div>
      </div>
    </FilterContext.Provider>
  );
}
